/**
 * Genie Chatbot API Client
 * Provides services for AI-powered gene queries and platform assistance
 */

import { API_BASE_URL } from '../config';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface ChatContext {
  current_gene?: string;
  therapeutic_area?: string;
  current_disease?: string;
}

export interface ChatAction {
  type: string;
  label: string;
  path?: string;
  value?: string;
}

export interface GeneQueryResult {
  gene_symbol: string;
  gene_name?: string;
  description?: string;
  therapeutic_areas: string[];
  diseases: Array<Record<string, unknown>>;
  expression_data: Array<Record<string, unknown>>;
}

export interface ChatResponse {
  message: string;
  suggestions: string[];
  gene_data?: GeneQueryResult;
  actions: ChatAction[];
  timestamp: string;
}

export interface ChatRequest {
  message: string;
  conversation_history: ChatMessage[];
  context?: ChatContext;
}

export interface SuggestionsResponse {
  suggestions: string[];
}

export interface AutocompleteResult {
  symbol: string;
  name: string;
  ensembl_id?: string;
}

export interface AutocompleteResponse {
  query: string;
  results: AutocompleteResult[];
  count: number;
}

export interface GeneValidationResponse {
  valid: boolean;
  symbol: string;
  name?: string;
  ensembl_id?: string;
  did_you_mean?: string;
  suggestions: string[];
}

export interface GenieHealthResponse {
  status: string;
  service: string;
  llm_configured: boolean;
  model: string;
}

class GenieClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Send a chat message to Genie
   */
  async chat(request: ChatRequest): Promise<ChatResponse> {
    return this.makeRequest<ChatResponse>('/api/genie/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get contextual suggestions
   */
  async getSuggestions(gene?: string, therapeuticArea?: string): Promise<SuggestionsResponse> {
    const params = new URLSearchParams();
    if (gene) params.append('gene', gene);
    if (therapeuticArea) params.append('therapeutic_area', therapeuticArea);
    
    const queryString = params.toString();
    return this.makeRequest<SuggestionsResponse>(
      `/api/genie/suggestions${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Check Genie service health
   */
  async healthCheck(): Promise<GenieHealthResponse> {
    return this.makeRequest<GenieHealthResponse>('/api/genie/health');
  }

  /**
   * Autocomplete gene symbols
   */
  async autocomplete(query: string, limit: number = 10): Promise<AutocompleteResponse> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());
    
    return this.makeRequest<AutocompleteResponse>(`/api/genie/autocomplete?${params.toString()}`);
  }

  /**
   * Validate a gene symbol and get suggestions if invalid
   */
  async validateGene(geneSymbol: string): Promise<GeneValidationResponse> {
    return this.makeRequest<GeneValidationResponse>(`/api/genie/genes/validate/${encodeURIComponent(geneSymbol)}`);
  }

  /**
   * Export content to PDF (returns HTML for printing)
   */
  async exportToPdf(content: string, title: string, genes: string[] = []): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/genie/export/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, title, genes, export_type: 'pdf' })
    });
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return response.text();
  }

  /**
   * Export content to CSV (returns CSV content)
   */
  async exportToCsv(content: string, title: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/genie/export/csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, title, export_type: 'csv' })
    });
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  /**
   * Export chat history to PDF
   */
  async exportChat(messages: Array<{role: string; content: string}>, title: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/genie/export/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, title })
    });
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return response.text();
  }

  /**
   * Export gene comparison report
   */
  async exportComparison(genes: string[], format: 'pdf' | 'csv' = 'pdf'): Promise<Blob | string> {
    const genesParam = genes.join(',');
    const response = await fetch(
      `${this.baseUrl}/api/genie/export/comparison/${encodeURIComponent(genesParam)}?format=${format}`
    );
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return format === 'csv' ? response.blob() : response.text();
  }
}

// Helper function to trigger file download
export function downloadFile(content: Blob | string, filename: string, type: string = 'text/plain'): void {
  console.log('downloadFile called with:', { filename, type, contentType: typeof content, contentLength: content instanceof Blob ? content.size : content.length });
  
  try {
    const blob = content instanceof Blob ? content : new Blob([content], { type });
    console.log('Blob created, size:', blob.size);
    
    const url = URL.createObjectURL(blob);
    console.log('Object URL created:', url);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    // Ensure the link is in the document
    document.body.appendChild(a);
    console.log('Link appended to document');
    
    // Use a more reliable click method
    if (typeof a.click === 'function') {
      a.click();
      console.log('Link clicked programmatically');
    } else {
      // Fallback for older browsers
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      a.dispatchEvent(event);
      console.log('Click event dispatched');
    }
    
    // Cleanup after a short delay
    setTimeout(() => {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
      URL.revokeObjectURL(url);
      console.log('Cleanup completed');
    }, 500);
    
    console.log(`File download triggered successfully: ${filename}`);
  } catch (error) {
    console.error('Download error:', error);
    // Try fallback with window.open
    try {
      const blob = content instanceof Blob ? content : new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      console.log('Fallback: opened in new window');
    } catch (fallbackError) {
      console.error('Fallback download also failed:', fallbackError);
      throw error;
    }
  }
}

// Helper function to open HTML in new window for printing as PDF
export function openPdfPreview(html: string, title: string = 'Report'): void {
  console.log('openPdfPreview called with title:', title, 'html length:', html.length);
  
  try {
    // Try to open a new window
    const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
    
    if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
      // Popup was blocked - fall back to download
      console.warn('Popup blocked or window closed, falling back to HTML download');
      const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_');
      downloadFile(html, `${safeTitle}.html`, 'text/html');
      alert('Popup was blocked by your browser. The report has been downloaded as an HTML file.\n\nOpen the file in your browser and press Ctrl+P (or Cmd+P on Mac) to save as PDF.');
      return;
    }
    
    console.log('New window opened successfully');
    
    // Write the HTML content
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.document.title = title;
    
    console.log('HTML content written to window');
    
    // Focus the window
    printWindow.focus();
    
    // Trigger print dialog after content loads
    setTimeout(() => {
      try {
        if (printWindow && !printWindow.closed) {
          printWindow.print();
          console.log('Print dialog triggered');
        }
      } catch (e) {
        console.warn('Auto-print failed (this is normal in some browsers):', e);
      }
    }, 1500);
    
    console.log('PDF preview opened successfully');
  } catch (error) {
    console.error('PDF preview error:', error);
    // Fallback: download as HTML
    const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(html, `${safeTitle}.html`, 'text/html');
    alert('Could not open preview window. The report has been downloaded as an HTML file.\n\nOpen the file in your browser and press Ctrl+P to save as PDF.');
  }
}

// Create and export a default instance
export const genieClient = new GenieClient();

// Export the class for custom instances
export default GenieClient;
